import { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import api from '../api/client';
import fetchTaskListPage from '../api/fetchTaskListPage';
import fetchTaskListAllPages from '../api/fetchTaskListAllPages';
import { useAuth } from '../context/AuthContext';
import TagTokenInput from '../components/TagTokenInput';
import Modal from '../components/Modal';
import MarkdownText from '../components/MarkdownText';
import StatementAttachmentLinks from '../components/StatementAttachmentLinks';
import { taskNavRef } from '../utils/taskNav';

const MAX_LIST_ITEMS = 200;
const PAGE_SIZE = 30;

/**
 * Каталог задач: теги (все обязательны), tag_any (хотя бы один), tag_not (исключить);
 * поиск по названию и условию; сохранение списка / открытие в редакторе.
 */
export default function TaskCatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [tasks, setTasks] = useState([]);
  const [tagCatalog, setTagCatalog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextPath, setNextPath] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [autoLoadNonce, setAutoLoadNonce] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    selectedTags: [],
    selectedTagsAny: [],
    selectedTagsNot: [],
  });

  const [saveTeacherOpen, setSaveTeacherOpen] = useState(false);
  const [saveStudentOpen, setSaveStudentOpen] = useState(false);
  const [listTitle, setListTitle] = useState('');
  const [studentId, setStudentId] = useState('');
  const [deadline, setDeadline] = useState('');
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionSaving, setActionSaving] = useState(false);
  const [catalogSortField, setCatalogSortField] = useState('site');
  const [catalogSortOrder, setCatalogSortOrder] = useState('asc');
  const [deletingBulk, setDeletingBulk] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState('');
  const [bulkTagsOpen, setBulkTagsOpen] = useState(false);
  const [bulkTagsAdd, setBulkTagsAdd] = useState([]);
  const [bulkTagsRemove, setBulkTagsRemove] = useState([]);
  const [bulkTagsSaving, setBulkTagsSaving] = useState(false);
  const [bulkTagsError, setBulkTagsError] = useState('');
  const [tagBulkMessage, setTagBulkMessage] = useState('');
  const [selectedTaskIds, setSelectedTaskIds] = useState(() => new Set());
  const [similarBulkSaving, setSimilarBulkSaving] = useState(false);
  const [similarBulkMessage, setSimilarBulkMessage] = useState('');
  const [similarBulkError, setSimilarBulkError] = useState('');

  const catalogParamsKey = searchParams.toString();
  const isStaffOrCatalogAdmin = Boolean(user?.is_staff || user?.is_admin);
  const catalogAutoSearchRef = useRef(false);
  const selectedCount = selectedTaskIds.size;

  useEffect(() => {
    let cancelled = false;
    async function loadCatalog() {
      try {
        const data = await fetchTaskListAllPages('/tasks/tags/?page_size=100');
        if (!cancelled) setTagCatalog(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setTagCatalog([]);
      }
    }
    loadCatalog();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const search = searchParams.get('search') || '';
    const slugs = searchParams.getAll('tag').map((s) => s.trim()).filter(Boolean);
    const resolved = slugs.map((slug) => {
      const found = tagCatalog.find((t) => t.slug === slug);
      return found || { id: slug, slug, name: slug, type: '' };
    });
    const slugsAny = searchParams.getAll('tag_any').map((s) => s.trim()).filter(Boolean);
    const resolvedAny = slugsAny.map((slug) => {
      const found = tagCatalog.find((t) => t.slug === slug);
      return found || { id: slug, slug, name: slug, type: '' };
    });
    const slugsNot = searchParams.getAll('tag_not').map((s) => s.trim()).filter(Boolean);
    const resolvedNot = slugsNot.map((slug) => {
      const found = tagCatalog.find((t) => t.slug === slug);
      return found || { id: slug, slug, name: slug, type: '' };
    });
    setFilters((prev) => ({
      ...prev,
      search,
      selectedTags: resolved,
      selectedTagsAny: resolvedAny,
      selectedTagsNot: resolvedNot,
    }));
    setCatalogSortField(searchParams.get('catalog_sort') === 'source' ? 'source' : 'site');
    setCatalogSortOrder(searchParams.get('catalog_order') === 'desc' ? 'desc' : 'asc');
  }, [catalogParamsKey, tagCatalog, searchParams]);

  useEffect(() => {
    if (!hasSubmitted) return;
    const el = document.getElementById('catalog-load-more-sentinel');
    if (!el || !hasMore || loadingMore) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const first = entries && entries[0];
        if (first?.isIntersecting) {
          setAutoLoadNonce((n) => n + 1);
        }
      },
      { root: null, rootMargin: '200px', threshold: 0.01 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasSubmitted, hasMore, loadingMore, tasks.length]);

  useEffect(() => {
    if (!hasSubmitted) return;
    if (!autoLoadNonce) return;
    loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoadNonce]);

  function buildCatalogParams(sortField, sortOrder) {
    const params = new URLSearchParams();
    if (filters.search.trim()) params.set('search', filters.search.trim());
    for (const t of filters.selectedTags || []) {
      if (t?.slug) params.append('tag', t.slug);
    }
    for (const t of filters.selectedTagsAny || []) {
      if (t?.slug) params.append('tag_any', t.slug);
    }
    for (const t of filters.selectedTagsNot || []) {
      if (t?.slug) params.append('tag_not', t.slug);
    }
    params.set('catalog_sort', sortField);
    params.set('catalog_order', sortOrder);
    return params;
  }

  function applyFilters(e) {
    e?.preventDefault();
    setHasSubmitted(true);
    const params = buildCatalogParams(catalogSortField, catalogSortOrder);
    setSearchParams(params);
    startSearch(params);
  }

  function updateCatalogSort(field, order) {
    setCatalogSortField(field);
    setCatalogSortOrder(order);
    if (!hasSubmitted) return;
    const p = buildCatalogParams(field, order);
    setSearchParams(p);
    startSearch(p);
  }

  function resetFilters() {
    setFilters({ search: '', selectedTags: [], selectedTagsAny: [], selectedTagsNot: [] });
    setSearchParams(new URLSearchParams());
    setCatalogSortField('site');
    setCatalogSortOrder('asc');
    setHasSubmitted(false);
    setError('');
    setTasks([]);
    setHasMore(false);
    setNextPath(null);
    setTotalCount(null);
    setSelectedTaskIds(new Set());
  }

  function toggleTaskSelected(id) {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAllVisibleTasks() {
    setSelectedTaskIds(new Set(tasks.map((t) => t.id)));
  }

  function clearTaskSelection() {
    setSelectedTaskIds(new Set());
  }

  function hasAnyFilter(params) {
    const sp = params instanceof URLSearchParams ? params : new URLSearchParams(params);
    const search = (sp.get('search') || '').trim();
    const tags = sp.getAll('tag').map((t) => (t || '').trim()).filter(Boolean);
    const tagsAny = sp.getAll('tag_any').map((t) => (t || '').trim()).filter(Boolean);
    const tagsNot = sp.getAll('tag_not').map((t) => (t || '').trim()).filter(Boolean);
    return Boolean(search) || tags.length > 0 || tagsAny.length > 0 || tagsNot.length > 0;
  }

  async function bulkDeleteShownTasks() {
    if (!tasks.length || !isStaffOrCatalogAdmin) return;
    const n = tasks.length;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Удалить ${n} задач(и) из базы? Связанные решения и пункты списков будут удалены каскадом.`)) {
      return;
    }
    setDeletingBulk(true);
    setDeleteMessage('');
    try {
      const res = await api.post('/tasks/editor/bulk-delete/', { task_ids: tasks.map((t) => t.id) });
      setDeleteMessage(`Удалено записей (с каскадами): ${res.data?.deleted ?? '—'}`);
      setTagBulkMessage('');
      setTasks([]);
      setHasMore(false);
      setNextPath(null);
      setTotalCount(0);
    } catch (e) {
      const d = e.response?.data;
      setDeleteMessage(
        typeof d?.detail === 'string' ? d.detail : 'Не удалось выполнить удаление.'
      );
    } finally {
      setDeletingBulk(false);
    }
  }

  function openBulkTagsModal() {
    setBulkTagsError('');
    setBulkTagsAdd([]);
    setBulkTagsRemove([]);
    setBulkTagsOpen(true);
  }

  async function submitBulkTags() {
    if (!tasks.length || !isStaffOrCatalogAdmin) return;
    if (!bulkTagsAdd.length && !bulkTagsRemove.length) {
      setBulkTagsError('Выберите хотя бы один тег для добавления или удаления.');
      return;
    }
    setBulkTagsSaving(true);
    setBulkTagsError('');
    try {
      const res = await api.post('/tasks/editor/bulk-tags/', {
        task_ids: tasks.map((t) => t.id),
        add_tag_ids: bulkTagsAdd.map((t) => t.id),
        remove_tag_ids: bulkTagsRemove.map((t) => t.id),
      });
      setTagBulkMessage(
        `Добавлено связей: ${res.data?.added ?? '—'}, снято: ${res.data?.removed ?? '—'}.`
      );
      setDeleteMessage('');
      setBulkTagsOpen(false);
      const params = buildCatalogParams(catalogSortField, catalogSortOrder);
      await startSearch(params);
    } catch (e) {
      const d = e.response?.data;
      setBulkTagsError(
        typeof d?.detail === 'string' ? d.detail : 'Не удалось изменить теги.'
      );
    } finally {
      setBulkTagsSaving(false);
    }
  }

  async function bulkLinkSelectedSimilar() {
    if (!isStaffOrCatalogAdmin || selectedCount < 2) return;
    setSimilarBulkSaving(true);
    setSimilarBulkError('');
    setSimilarBulkMessage('');
    try {
      const res = await api.post('/tasks/editor/bulk-similar/', {
        task_ids: Array.from(selectedTaskIds),
      });
      setSimilarBulkMessage(
        `Связи аналогов: создано новых ${res.data?.created ?? '—'}, уже было ${res.data?.already_linked ?? '—'}.`,
      );
      setDeleteMessage('');
      setTagBulkMessage('');
    } catch (e) {
      const d = e.response?.data;
      setSimilarBulkError(typeof d?.detail === 'string' ? d.detail : 'Не удалось создать связи.');
    } finally {
      setSimilarBulkSaving(false);
    }
  }

  async function bulkRemoveSimilarAmongSelected() {
    if (!isStaffOrCatalogAdmin || selectedCount < 2) return;
    setSimilarBulkSaving(true);
    setSimilarBulkError('');
    setSimilarBulkMessage('');
    try {
      const res = await api.post('/tasks/editor/bulk-similar-remove/', {
        task_ids: Array.from(selectedTaskIds),
      });
      setSimilarBulkMessage(`Удалено связей между выбранными: ${res.data?.removed ?? '—'}.`);
      setDeleteMessage('');
      setTagBulkMessage('');
    } catch (e) {
      const d = e.response?.data;
      setSimilarBulkError(typeof d?.detail === 'string' ? d.detail : 'Не удалось снять связи.');
    } finally {
      setSimilarBulkSaving(false);
    }
  }

  async function startSearch(params) {
    setError('');
    setTasks([]);
    setHasMore(false);
    setNextPath(null);
    setTotalCount(null);
    setAutoLoadNonce(0);
    setSelectedTaskIds(new Set());

    if (!hasAnyFilter(params)) {
      setError('Введите условия и нажмите «Применить».');
      return;
    }

    setLoading(true);
    try {
      const qs = params.toString();
      // include_statement=1: в публичном каталоге отдаём полное условие (markdown), а не только statement_preview.
      const path = qs ? `/tasks/?${qs}&include_statement=1` : '/tasks/?include_statement=1';
      const { results, nextPath: next, count } = await fetchTaskListPage(path, { pageSize: PAGE_SIZE });
      setTasks(results);
      setNextPath(next);
      setHasMore(Boolean(next));
      setTotalCount(typeof count === 'number' ? count : null);
    } catch {
      setError('Не удалось загрузить каталог задач.');
      setTasks([]);
      setHasMore(false);
      setNextPath(null);
      setTotalCount(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const sp = new URLSearchParams(catalogParamsKey);
    if (!hasAnyFilter(sp)) {
      catalogAutoSearchRef.current = false;
      return;
    }
    if (catalogAutoSearchRef.current) return;
    catalogAutoSearchRef.current = true;
    setHasSubmitted(true);
    void startSearch(sp);
  }, [catalogParamsKey]);

  async function loadMore() {
    if (!hasSubmitted || loadingMore || loading || !hasMore || !nextPath) return;
    setLoadingMore(true);
    setError('');
    try {
      const { results, nextPath: next, count } = await fetchTaskListPage(nextPath, { pageSize: PAGE_SIZE });
      setTasks((prev) => [...prev, ...(results || [])]);
      setNextPath(next);
      setHasMore(Boolean(next));
      if (typeof count === 'number') setTotalCount(count);
    } catch {
      setError('Не удалось загрузить следующую страницу.');
    } finally {
      setLoadingMore(false);
    }
  }

  const itemsPayload = useMemo(() => {
    const slice = tasks.slice(0, MAX_LIST_ITEMS);
    return slice.map((t, index) => ({
      task_id: t.id,
      position: index,
      teacher_note: '',
    }));
  }, [tasks]);

  const isTeacher = Boolean(user?.is_teacher);
  const canSelfList = Boolean(user);

  useEffect(() => {
    if (!saveTeacherOpen || !isTeacher) return;
    let cancelled = false;
    setStudentsLoading(true);
    api
      .get('/teacher/students/')
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
        if (!cancelled) setStudents(data);
      })
      .catch(() => {
        if (!cancelled) setStudents([]);
      })
      .finally(() => {
        if (!cancelled) setStudentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [saveTeacherOpen, isTeacher]);

  function openTeacherModal() {
    setActionError('');
    setListTitle('');
    setStudentId('');
    setDeadline('');
    setSaveTeacherOpen(true);
  }

  function openStudentModal() {
    setActionError('');
    setListTitle('');
    setSaveStudentOpen(true);
  }

  async function submitTeacherList() {
    if (!listTitle.trim()) {
      setActionError('Введите название списка.');
      return;
    }
    if (!studentId) {
      setActionError('Выберите ученика.');
      return;
    }
    if (tasks.length === 0) {
      setActionError('Нет задач для сохранения.');
      return;
    }
    setActionSaving(true);
    setActionError('');
    try {
      const payload = {
        title: listTitle.trim(),
        student: Number(studentId),
        deadline_at: deadline || null,
        items: itemsPayload,
      };
      const res = await api.post('/teacher/task-lists/', payload);
      const id = res.data?.id;
      setSaveTeacherOpen(false);
      const first = taskNavRef(tasks[0]);
      if (id && first) {
        navigate(`/work/${first}?list=${id}`);
      }
    } catch (e) {
      const msg = e.response?.data;
      if (typeof msg === 'object' && msg !== null) {
        const first = Object.entries(msg)[0];
        setActionError(
          first ? `${first[0]}: ${Array.isArray(first[1]) ? first[1][0] : first[1]}` : 'Ошибка сохранения.'
        );
      } else {
        setActionError('Не удалось сохранить список.');
      }
    } finally {
      setActionSaving(false);
    }
  }

  async function submitStudentList() {
    if (!listTitle.trim()) {
      setActionError('Введите название списка.');
      return;
    }
    if (tasks.length === 0) {
      setActionError('Нет задач для сохранения.');
      return;
    }
    setActionSaving(true);
    setActionError('');
    try {
      const payload = {
        title: listTitle.trim(),
        items: itemsPayload,
      };
      const res = await api.post('/tasks/lists/', payload);
      const id = res.data?.id;
      setSaveStudentOpen(false);
      const first = taskNavRef(tasks[0]);
      if (id && first) {
        navigate(`/work/${first}?list=${id}`);
      }
    } catch (e) {
      const msg = e.response?.data;
      if (typeof msg === 'object' && msg !== null) {
        const first = Object.entries(msg)[0];
        setActionError(
          first ? `${first[0]}: ${Array.isArray(first[1]) ? first[1][0] : first[1]}` : 'Ошибка.'
        );
      } else {
        setActionError('Не удалось создать список.');
      }
    } finally {
      setActionSaving(false);
    }
  }

  const overLimit = tasks.length > MAX_LIST_ITEMS;

  return (
    <div className="task-catalog-page">
      <div className="catalog-page-header">
        <h1 className="catalog-page-title">Каталог задач</h1>
        <p className="catalog-page-lead">
          Обязательные теги: задача должна содержать все указанные. «Хотя бы один» расширяет выбор. «Исключить» убирает
          задачи с этими тегами.
        </p>
      </div>

      <section className="catalog-filters">
        <form onSubmit={applyFilters} className="catalog-filters-form">
          <div className="form-group">
            <label>Поиск по названию и условию</label>
            <input
              type="text"
              className="form-input"
              placeholder="Например, «циклы» или фраза из текста задачи"
              value={filters.search}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, search: e.target.value }))
              }
            />
          </div>
          <TagTokenInput
            label="Теги (все обязательны)"
            value={filters.selectedTags}
            onChange={(selectedTags) =>
              setFilters((prev) => ({ ...prev, selectedTags }))
            }
          />
          <TagTokenInput
            label="Хотя бы один из тегов"
            value={filters.selectedTagsAny}
            onChange={(selectedTags) =>
              setFilters((prev) => ({ ...prev, selectedTagsAny: selectedTags }))
            }
          />
          <TagTokenInput
            label="Исключить теги"
            value={filters.selectedTagsNot}
            onChange={(selectedTags) =>
              setFilters((prev) => ({ ...prev, selectedTagsNot: selectedTags }))
            }
          />
          <div className="catalog-sort-row">
            <div className="form-group catalog-sort-field">
              <label htmlFor="catalog-sort-field">Сортировка</label>
              <select
                id="catalog-sort-field"
                className="form-select"
                value={catalogSortField}
                onChange={(e) =>
                  updateCatalogSort(e.target.value === 'source' ? 'source' : 'site', catalogSortOrder)
                }
              >
                <option value="site">Номер на сайте</option>
                <option value="source">Номер в источнике</option>
              </select>
            </div>
            <div className="form-group catalog-sort-field">
              <label htmlFor="catalog-sort-order">Направление</label>
              <select
                id="catalog-sort-order"
                className="form-select"
                value={catalogSortOrder}
                onChange={(e) =>
                  updateCatalogSort(
                    catalogSortField,
                    e.target.value === 'desc' ? 'desc' : 'asc'
                  )
                }
              >
                <option value="asc">По возрастанию</option>
                <option value="desc">По убыванию</option>
              </select>
            </div>
          </div>
          <div className="catalog-filters-actions">
            <button type="submit" className="btn btn-primary">
              Применить
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={resetFilters}
            >
              Сбросить
            </button>
            {filters.selectedTags?.length === 1 &&
            filters.selectedTags[0]?.slug &&
            !(filters.selectedTagsAny && filters.selectedTagsAny.length) &&
            !(filters.selectedTagsNot && filters.selectedTagsNot.length) &&
            !filters.search?.trim() ? (
              <Link
                to={`/catalog/print/${encodeURIComponent(filters.selectedTags[0].slug)}?${new URLSearchParams({
                  catalog_sort: catalogSortField,
                  catalog_order: catalogSortOrder,
                }).toString()}`}
                className="btn btn-outline catalog-print-link"
              >
                Для печати по тегу
              </Link>
            ) : null}
          </div>
        </form>
      </section>

      {error && <div className="alert alert-error">{error}</div>}
      {deleteMessage && <div className="alert alert-info">{deleteMessage}</div>}
      {tagBulkMessage && <div className="alert alert-info">{tagBulkMessage}</div>}
      {similarBulkMessage && <div className="alert alert-info">{similarBulkMessage}</div>}
      {similarBulkError && <div className="alert alert-error">{similarBulkError}</div>}

      {!loading && hasSubmitted && tasks.length > 0 && canSelfList && (
        <div className="catalog-bulk-actions">
          {isTeacher && (
            <button type="button" className="btn btn-outline btn-sm" onClick={openTeacherModal}>
              Сохранить найденные как список ученику
            </button>
          )}
          <button type="button" className="btn btn-primary btn-sm" onClick={openStudentModal}>
            Открыть найденные для решения
          </button>
          {isStaffOrCatalogAdmin && tasks.length > 0 && (
            <>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                disabled={bulkTagsSaving}
                onClick={openBulkTagsModal}
              >
                Изменить теги у показанных ({tasks.length})
              </button>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                style={{ borderColor: 'var(--error)', color: 'var(--error)' }}
                disabled={deletingBulk}
                onClick={bulkDeleteShownTasks}
              >
                {deletingBulk ? 'Удаление…' : `Удалить показанные из базы (${tasks.length})`}
              </button>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                disabled={similarBulkSaving || selectedCount < 2}
                onClick={() => void bulkLinkSelectedSimilar()}
                title="Попарно связать все выбранные задачи как аналоги"
              >
                {similarBulkSaving ? '…' : `Связать выбранные как аналоги (${selectedCount})`}
              </button>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                disabled={similarBulkSaving || selectedCount < 2}
                onClick={() => void bulkRemoveSimilarAmongSelected()}
                title="Удалить связи SimilarTask, где оба конца среди выбранных"
              >
                {similarBulkSaving ? '…' : `Убрать связи между выбранными (${selectedCount})`}
              </button>
            </>
          )}
          {overLimit && (
            <span className="catalog-bulk-hint text-secondary">
              В список попадут первые {MAX_LIST_ITEMS} из {tasks.length} задач.
            </span>
          )}
        </div>
      )}

      {!hasSubmitted ? (
        <p className="text-secondary">Введите условия и нажмите «Применить», чтобы показать задачи.</p>
      ) : loading ? (
        <p className="text-secondary">Загрузка задач...</p>
      ) : tasks.length === 0 ? (
        <p className="text-secondary">
          Задач пока нет или фильтр слишком строгий. Попробуйте изменить условия.
        </p>
      ) : (
        <section className="catalog-list">
          <div className="catalog-list-header">
            <span>
              Показано: {tasks.length}
              {typeof totalCount === 'number' ? ` из ${totalCount}` : ''}
            </span>
            {isStaffOrCatalogAdmin ? (
              <div className="catalog-list-selection">
                <span className="text-secondary catalog-selection-count">Выбрано: {selectedCount}</span>
                <button type="button" className="btn btn-outline btn-xs" onClick={selectAllVisibleTasks}>
                  Выбрать все на экране
                </button>
                <button type="button" className="btn btn-outline btn-xs" onClick={clearTaskSelection}>
                  Снять выбор
                </button>
              </div>
            ) : null}
          </div>
          <ul className="catalog-rows">
            {tasks.map((task) => (
              <li key={task.id} className="catalog-row">
                {isStaffOrCatalogAdmin ? (
                  <label className="catalog-row-check">
                    <input
                      type="checkbox"
                      checked={selectedTaskIds.has(task.id)}
                      onChange={() => toggleTaskSelected(task.id)}
                      aria-label={`Выбрать задачу ${taskNavRef(task)}`}
                    />
                  </label>
                ) : null}
                <div className="catalog-row-main">
                  <div className="catalog-row-title-line">
                    <span className="catalog-row-id">
                      {task.source_number != null && task.source_number !== ''
                        ? String(task.source_number)
                        : taskNavRef(task)}
                    </span>
                    <Link to={`/work/${taskNavRef(task)}`} className="catalog-row-title">
                      {task.title || taskNavRef(task)}
                    </Link>
                  </div>
                  <StatementAttachmentLinks attachments={task.statement_attachments} variant="catalog" />
                  {task.statement ? (
                    <div className="catalog-row-preview">
                      <MarkdownText className="statement-text" text={task.statement} />
                    </div>
                  ) : task.statement_preview ? (
                    <p className="catalog-row-preview">{task.statement_preview}</p>
                  ) : null}
                  {task.tags && task.tags.length > 0 && (
                    <div className="task-tags catalog-row-tags">
                      {task.tags.map((tag) => (
                        <span key={tag.id} className="tag">
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="catalog-row-side">
                  <span
                    className={`catalog-row-badge ${task.is_verified ? 'is-verified' : 'is-draft'}`}
                  >
                    {task.is_verified ? 'Проверена' : 'Черновик'}
                  </span>
                  {task.created_at && (
                    <span className="text-secondary catalog-row-date">
                      {new Date(task.created_at).toLocaleDateString('ru-RU')}
                    </span>
                  )}
                  <Link
                    to={`/work/${taskNavRef(task)}`}
               className="btn btn-sm btn-primary catalog-row-btn"
                  >
                    Открыть
                  </Link>
                </div>
              </li>
            ))}
          </ul>
          <div style={{ marginTop: '0.75rem' }}>
            {hasMore ? (
              <>
                <div className="text-secondary" style={{ fontSize: '0.85rem', marginBottom: '0.35rem' }}>
                  В каталоге есть ещё задачи — прокрутите вниз для подгрузки.
                </div>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  disabled={loadingMore}
                  onClick={loadMore}
                >
                  {loadingMore ? 'Загрузка…' : 'Загрузить ещё'}
                </button>
                <div id="catalog-load-more-sentinel" style={{ height: '1px' }} />
              </>
            ) : (
              <div className="text-secondary" style={{ fontSize: '0.85rem' }}>
                Это все задачи по текущему фильтру.
              </div>
            )}
          </div>
        </section>
      )}

      <Modal
        open={saveTeacherOpen}
        onClose={() => !actionSaving && setSaveTeacherOpen(false)}
        title="Список ученику"
      >
        <div className="catalog-modal-form">
          {actionError && <div className="alert alert-error">{actionError}</div>}
          <div className="form-group">
            <label>Название</label>
            <input
              className="form-input"
              value={listTitle}
              onChange={(e) => setListTitle(e.target.value)}
              placeholder="Например, Подготовка к №17"
            />
          </div>
          <div className="form-group">
            <label>Ученик</label>
            {studentsLoading ? (
              <p className="text-secondary">Загрузка…</p>
            ) : (
              <select
                className="form-select"
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
              >
                <option value="">— выберите —</option>
                {students.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.username}
                    {s.first_name ? ` (${s.first_name})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="form-group">
            <label>Дедлайн (необязательно)</label>
            <input
              type="date"
              className="form-input"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
            />
          </div>
          <p className="text-secondary catalog-modal-note">
            Будет сохранено задач: {Math.min(tasks.length, MAX_LIST_ITEMS)}
            {tasks.length > MAX_LIST_ITEMS ? ` из ${tasks.length}` : ''}.
          </p>
          <div className="catalog-modal-actions">
            <button
              type="button"
              className="btn btn-outline"
              disabled={actionSaving}
              onClick={() => setSaveTeacherOpen(false)}
            >
              Отмена
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={actionSaving}
              onClick={submitTeacherList}
            >
              {actionSaving ? 'Сохранение…' : 'Создать и открыть'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={bulkTagsOpen}
        onClose={() => !bulkTagsSaving && setBulkTagsOpen(false)}
        title="Пакетное изменение тегов"
      >
        <div className="catalog-modal-form">
          {bulkTagsError && <div className="alert alert-error">{bulkTagsError}</div>}
          <p className="text-secondary catalog-modal-note" style={{ marginBottom: '0.75rem' }}>
            Будут затронуты все показанные задачи ({tasks.length}). Уровень у новых связей тега — «стандартный»
            (как при импорте по умолчанию).
          </p>
          <TagTokenInput
            label="Добавить теги"
            placeholder="Начните вводить название тега…"
            value={bulkTagsAdd}
            onChange={setBulkTagsAdd}
          />
          <TagTokenInput
            label="Снять теги"
            placeholder="Теги, которые убрать у всех показанных задач"
            value={bulkTagsRemove}
            onChange={setBulkTagsRemove}
          />
          <div className="catalog-modal-actions">
            <button
              type="button"
              className="btn btn-outline"
              disabled={bulkTagsSaving}
              onClick={() => setBulkTagsOpen(false)}
            >
              Отмена
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={bulkTagsSaving}
              onClick={submitBulkTags}
            >
              {bulkTagsSaving ? 'Сохранение…' : 'Применить'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={saveStudentOpen}
        onClose={() => !actionSaving && setSaveStudentOpen(false)}
        title="Свой список для решения"
      >
        <div className="catalog-modal-form">
          {actionError && <div className="alert alert-error">{actionError}</div>}
          <div className="form-group">
            <label>Название списка</label>
            <input
              className="form-input"
              value={listTitle}
              onChange={(e) => setListTitle(e.target.value)}
              placeholder="Например, Разбор на выходные"
            />
          </div>
          <p className="text-secondary catalog-modal-note">
            Список появится в «Мои списки задач». Будет добавлено задач:{' '}
            {Math.min(tasks.length, MAX_LIST_ITEMS)}
            {tasks.length > MAX_LIST_ITEMS ? ` из ${tasks.length}` : ''}.
          </p>
          <div className="catalog-modal-actions">
            <button
              type="button"
              className="btn btn-outline"
              disabled={actionSaving}
              onClick={() => setSaveStudentOpen(false)}
            >
              Отмена
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={actionSaving}
              onClick={submitStudentList}
            >
              {actionSaving ? 'Создание…' : 'Создать и открыть в редакторе'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
